import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import { FaEye, FaEdit, FaTrash, FaPlus, FaSearch } from "react-icons/fa";
import axios from "axios";
import toast from "react-hot-toast";
import { confirmAlert } from "react-confirm-alert";

import Modal from "../../../components/Modal/Modal";
import ErrorBoundaryRoutes from "../../../components/shared/ErrorBoundaryRoutes";
import { gbState } from "../../../components/shared/GlobalState";
import CategoryView from "./CategoryView";

const CategoryListChild = () => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [data, setData] = useState([]);
  const [{ errorLogApi, user }] = useAtom(gbState);

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const handleDelete = (row) => {
    confirmAlert({
      title: "Confirm Deletion",
      message: "Are you sure you want to delete this Category?",
      buttons: [
        {
          label: "Yes",
          onClick: () => deleteRecord(row),
        },
        {
          label: "No",
        },
      ],
    });
  };

  const deleteRecord = async (row) => {
    const userToken = localStorage.getItem("token");
    const config = {
      headers: { Authorization: `Bearer ${userToken}` },
    };

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/category/delete/${row._id}`,
        config
      );
      toast.success("Category Deleted Successfully.");
      getDataFromServer();
    } catch (err) {
      errorLogApi(err, "CategoryListChild", "/category");
    }
  };

  const getDataFromServer = async () => {
    const userToken = localStorage.getItem("token");
    const urlLink = `${process.env.REACT_APP_API_URL}/category/${user.church_id}`;

    try {
      const response = await axios.get(urlLink, {
        headers: { Authorization: `Bearer ${userToken}` },
      });
      const updatedData = response.data.Categorys.map((data) => ({
        ...data,
        id: data._id,
        category: data.category_name,
        type: data.category_type,
      }));
      setData(updatedData);
    } catch (err) {
      console.log("error api call ", err.response?.data?.message);
    }
  };

  useEffect(() => {
    getDataFromServer();
  }, []);

  const filteredData = useMemo(() => {
    return data.filter((category) =>
      Object.values(category).some((value) =>
        value.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [data, searchTerm]);

  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  return (
    <div className="w-full px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Category List</h2>
        <Button
          variant="contained"
          color="primary"
          startIcon={<FaPlus />}
          onClick={() => navigate("/categoryadd")}
        >
          Add Category
        </Button>
      </div>

      <div className="mb-4">
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search categories..."
          InputProps={{
            startAdornment: <FaSearch className="mr-2 text-gray-400" />,
          }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Action</TableCell>
              <TableCell>Category Name</TableCell>
              <TableCell>Category Type</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row) => (
              <TableRow key={row._id}>
                <TableCell>
                  <div className="flex items-center space-x-1">
                    <Tooltip title="View details">
                      <IconButton
                        size="small"
                        onClick={() => {
                          toggleModal();
                          setCurrentRecord(row);
                        }}
                      >
                        <FaEye className="text-blue-600" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigate("/category/update", { state: row })
                        }
                      >
                        <FaEdit className="text-gray-600" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(row)}
                      >
                        <FaTrash className="text-red-600" />
                      </IconButton>
                    </Tooltip>
                  </div>
                </TableCell>
                <TableCell>{row.category_name}</TableCell>
                <TableCell>{row.category_type}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination controls */}
      <div className="flex items-center justify-between mt-4">
        <div>
          <span>Rows per page: </span>
          <select
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(Number(e.target.value))}
          >
            {[5, 10, 25, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Button
            disabled={page === 0}
            onClick={() => setPage((prev) => prev - 1)}
          >
            Previous
          </Button>
          <Button
            disabled={paginatedData.length < rowsPerPage}
            onClick={() => setPage((prev) => prev + 1)}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Modal */}
      <ErrorBoundaryRoutes route="/category/" page="ModalView">
        {showModal && (
          <Modal onClose={() => setShowModal(false)}>
            <CategoryView currentRecord={currentRecord} />
          </Modal>
        )}
      </ErrorBoundaryRoutes>
    </div>
  );
};

export default CategoryListChild;
